// The site's data, from the API worker (workers/data-pipeline.js). Read only.
// The lists every visit needs, and each member's and candidate's detail,
// come first from plain files built with the site (scripts/site/
// prerender.mjs: no daily limit, unlike the worker's 100,000 requests), and
// from the API when a file isn't there.

export const API_BASE_URL = 'https://taskforce-purple-api.dev-a4b.workers.dev/api';

const cache = new Map();

// One request per resource per page load; a failed one is forgotten so it
// can be retried
function once(key, load) {
  if (!cache.has(key)) {
    const request = load();
    request.catch(() => cache.delete(key));
    cache.set(key, request);
  }
  return cache.get(key);
}

async function getJson(url) {
  const response = await fetch(url);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

/** A list built with the site, else the API's */
async function built(file, url) {
  try {
    const data = await getJson(`/data/${file}`);
    if (data) {
      return data;
    }
  } catch {
    // Not built (development, or a failed build): the API has it
  }
  return getJson(url);
}

export const api = {
  /** Every member with their published grade (KV members:list). */
  members: () =>
    once('members', async () => {
      const data = await built('members.json', `${API_BASE_URL}/members`);
      if (!data?.members?.length) {
        throw new Error('No members published');
      }
      return data;
    }),

  /** One member's money trail, donors and evidence (D1 results). */
  memberDetail: id =>
    once(`member:${id}`, () =>
      built(
        `member/${encodeURIComponent(id)}.json`,
        `${API_BASE_URL}/member-detail?bioguideId=${encodeURIComponent(id)}`
      )
    ),

  /** A candidate who isn't a sitting member: the same detail as a member's. */
  candidateDetail: id =>
    once(`candidate:${id}`, () =>
      built(
        `candidate/${encodeURIComponent(id)}.json`,
        `${API_BASE_URL}/candidate-detail?id=${encodeURIComponent(id)}`
      )
    ),

  /**
   * The 2026 races (ROADMAP Phase E). Null until November's field is
   * published (Wikipedia's lists matched to the FEC: races.mjs and
   * lib/ballot.mjs). VITE_RACES_URL: a local file instead, for checking
   * the pages in development.
   */
  races: () =>
    once('races', async () => {
      const data = import.meta.env.VITE_RACES_URL
        ? await getJson(import.meta.env.VITE_RACES_URL)
        : await built('races.json', `${API_BASE_URL}/races`);
      const live = data?.field === 'ballot' || (import.meta.env.DEV && data?.field === 'test');
      return live && data.races?.length ? data : null;
    }),

  /**
   * Outside spending by person (scripts/site/outside.mjs): built with the
   * site only, from the FEC's files; null when there's no file (development,
   * VITE_OUTSIDE_URL to check the pages with one)
   */
  outside: () =>
    once('outside', () =>
      getJson(import.meta.env.VITE_OUTSIDE_URL || '/data/outside.json').catch(() => null)
    ),
  /** Who holds each seat (scripts/site/seats.mjs), for the results page */
  seats: () => once('seats', () => getJson('/data/seats.json').catch(() => null)),
};

// Static files built from the Census Bureau's (scripts/geo/build-geo.mjs)
export const geoFiles = {
  zips: () => once('geo:zip', () => getJson('/geo/zip.json')),
  index: () => once('geo:index', () => getJson('/geo/index.json')),
  state: code => once(`geo:${code}`, () => getJson(`/geo/cd/${code}.json`)),
  // The 2026 lines, for the states that redrew (scripts/geo/build-geo-2026.mjs)
  zips2026: () => once('geo:zip:2026', () => getJson('/geo/2026/zip.json')),
  state2026: code => once(`geo:${code}:2026`, () => getJson(`/geo/2026/cd/${code}.json`)),
};
